import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { AccountInfo } from '@azure/msal-browser';
import { clearAutoSignIn } from './auto-signin.js';
import {
  acquireToken,
  initialiseMsal,
  initialsFor,
  signIn as msalSignIn,
  signOut as msalSignOut,
  type AuthConfig,
} from './msal.js';

/**
 * Authentication state for the SPA.
 *
 * Deliberately holds no token in React state. Tokens live only in MSAL's
 * sessionStorage cache and are fetched per request through `getToken`, so a
 * component tree that logs its props cannot leak one, and a refresh in another
 * tab is picked up immediately.
 */

export interface AuthUser {
  readonly oid: string;
  readonly upn: string;
  readonly displayName: string;
  readonly initials: string;
}

export type AuthStatus = 'initialising' | 'signed-out' | 'signed-in' | 'error';

interface AuthContextValue {
  readonly status: AuthStatus;
  readonly user: AuthUser | null;
  readonly error: string | null;
  readonly signIn: () => void;
  readonly signOut: () => void;
  /** Resolves a fresh access token, refreshing silently when needed. */
  readonly getToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function toUser(account: AccountInfo): AuthUser {
  return {
    // `localAccountId` is the Entra object ID, and matches the `oid` claim the
    // API keys users on.
    oid: account.localAccountId,
    upn: account.username,
    displayName: account.name ?? account.username,
    initials: initialsFor(account),
  };
}

export function AuthProvider({
  config,
  children,
}: {
  config: AuthConfig;
  children: ReactNode;
}): ReactElement {
  const [status, setStatus] = useState<AuthStatus>('initialising');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    initialiseMsal(config)
      .then((account) => {
        if (cancelled) return;
        if (account) {
          // This tab has now proved it can complete a redirect, so release
          // the one-shot guard. A token expiring an hour from now should
          // send the user back to Entra, not strand them on a sign-in page.
          clearAutoSignIn();
          setUser(toUser(account));
          setStatus('signed-in');
        } else {
          setStatus('signed-out');
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // A failure here is usually a misconfigured redirect URI or a blocked
        // third-party cookie. Surfacing it beats an endless spinner.
        setError(err instanceof Error ? err.message : 'Sign-in failed');
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [config]);

  const signIn = useCallback(() => {
    void msalSignIn(config).catch((err: unknown) => {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
      setStatus('error');
    });
  }, [config]);

  const signOut = useCallback(() => {
    void msalSignOut().catch(() => {
      // logoutRedirect navigates away; a rejection here means the navigation
      // was blocked, in which case clearing local state is the best we can do.
      setUser(null);
      setStatus('signed-out');
    });
  }, []);

  const getToken = useCallback(() => acquireToken(config), [config]);

  const value = useMemo(
    () => ({ status, user, error, signIn, signOut, getToken }),
    [status, user, error, signIn, signOut, getToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an AuthProvider');
  return ctx;
}
